import { ParsedUrlQuery } from 'querystring';
import React, { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { toCamelCaseDeep } from 'src/utils/transform';
import { defineMessages, useIntl } from 'react-intl';
import { Center, Header, LinkButton, Stack } from 'src/components';
import { useCookiesAreEnabled } from 'src/cookies';
import { stringifyUrl } from 'src/utils/stringifyUrl';
import Head from 'next/head';
import RedirectIfAuth from 'src/utilityComponents/RedirectIfAuth';
import { useQuery } from '@tanstack/react-query';
import { PathApi } from 'src/api';
import { ROUTES } from 'src/constants/routes.const';
import { useUserContext } from 'src/context/user';
import { saveLastLanguagePair } from 'src/utils/language/saveLastLanguagePair';
import useSafePush from 'src/hooks/useSafePush';
import { PathOverviewResponse } from 'src/api/path/types';
import { ONE_MINUTE } from 'src/constants/times.const';
import { BienvenueProvider } from './state';
import * as S from './styles';
import BienvenueRouter from './Router';

const msgs = defineMessages({
  cookieError: { id: 'login_page_error_cookies' },
  login: { id: 'sign_in' },
  pageHeader: { id: 'bienvenue_page_head' },
});

interface BienvenueProps {
  source?: string;
  target?: string;
  next?: string;
}

const Bienvenue = () => {
  const { query } = useRouter();
  const { source, target, next } = getPropsFromQuery(query);
  const { safePush } = useSafePush();
  const { user } = useUserContext();

  const signInHref = useMemo(() => stringifyUrl(ROUTES.signIn, next ? { next } : null), [next]);

  const { data: pathsOverview, isLoading } = useQuery<PathOverviewResponse | null>({
    queryKey: ['pathsOverview', source],
    queryFn: () => {
      if (!source) return null;
      return PathApi.getPathsOverview({
        source_language_locale: source,
      });
    },
    staleTime: ONE_MINUTE,
  });

  const languagePairId = pathsOverview?.paths.filter(
    path => path.targetLanguageLocale === target,
  )[0]?.languagePairId;

  const intl = useIntl();
  const cookiesAreEnabled = useCookiesAreEnabled();

  useEffect(() => {
    if (languagePairId) {
      saveLastLanguagePair(languagePairId, source, target);
    }
  }, [languagePairId, source, target]);

  /* SEO uses cases for existing users */
  useEffect(() => {
    async function enrollUser() {
      /* istanbul ignore if: languagePairId it not null here */
      if (!languagePairId) return;
      saveLastLanguagePair(languagePairId, source, target);
      /* eslint-disable promise/catch-or-return */
      PathApi.enroll(languagePairId)
        .catch(() => {
          /* do nothing, user is already enrolled */
        })
        .finally(async () => {
          await safePush(next || ROUTES.dashboard);
        });
    }
    if (!!user && !!languagePairId) {
      void enrollUser();
    }
  }, [user, languagePairId, safePush, next, source, target]);

  /*
    For some reason, when we define title in such format:
    <title>{intl.formatMessage(msgs.pageHeader)} - Memrise</title>

    The result is following:
    <title>Create a free account<!-- --> - Memrise</title>

    To fix such behavior we moved the title defining to a separate variable
    and used this variable inside jsx. You can read more about it here:
    https://github.com/vercel/next.js/discussions/38256#discussioncomment-3070196
  */
  const title = `${intl.formatMessage(msgs.pageHeader)} - Memrise`;

  if (isLoading) {
    return <S.Loader />;
  }

  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>
      <RedirectIfAuth slug={next}>
        <>
          <Header logoUrl="https://www.memrise.com/">
            <S.ButtonWrapper data-testid="sign-up-page">
              <LinkButton href={signInHref} buttonType="primary">
                {intl.formatMessage(msgs.login)}
              </LinkButton>
            </S.ButtonWrapper>
          </Header>

          {!cookiesAreEnabled ? (
            <S.Background>
              <Stack $paddingY="s8">
                <Center $paddingX="s8" $maxWidth="md">
                  <S.ErrorContainer>
                    <p>{intl.formatMessage(msgs.cookieError)}</p>
                  </S.ErrorContainer>
                </Center>
              </Stack>
            </S.Background>
          ) : (
            <BienvenueProvider>
              <S.BienvenueContainer>
                <BienvenueRouter
                  nextUrl={next}
                  sourceLanguage={source}
                  targetLanguage={target}
                  languagePairId={languagePairId}
                />
              </S.BienvenueContainer>
            </BienvenueProvider>
          )}
        </>
      </RedirectIfAuth>
    </>
  );
};

const getOptionalStringFromQueryParam = (queryParam: string | string[] | undefined) => {
  return Array.isArray(queryParam) ? queryParam[0] : queryParam;
};

export const getPropsFromQuery = (query: ParsedUrlQuery): BienvenueProps => {
  let { next, source, target } = toCamelCaseDeep(query);
  source = getOptionalStringFromQueryParam(source);
  target = getOptionalStringFromQueryParam(target);
  const sourceAndTarget = source && target ? { source, target } : {};

  next = getOptionalStringFromQueryParam(next);
  const isNextBlacklisted = next?.startsWith('/community/') || next?.includes('group');

  return {
    ...sourceAndTarget,
    ...(next && !isNextBlacklisted ? { next } : {}),
  };
};

export default Bienvenue;
